// Bandeja de aprobaciones de ventas (pedido de Rodrigo, 2026-10-09): lo que espera firma, con el
// porqué del bloqueo, para autorizar una o varias de una vez. Autorizar no pide motivo: queda
// registrado quién autorizó y cuándo. Quién puede autorizar lo deciden los permisos (el backend
// lo dice por fila y lo vuelve a exigir al firmar). Cada nota se firma por separado y auditada.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { Modal } from '../comun/Modal';
import { fechaHora, NOMBRE_CONDICION, pesos, rut } from '../comun/formato';
import { MOTIVOS_CREDITO, NOMBRE_AMBITO, presentarError } from '../errores/catalogo';
import { useSesion } from '../sesion/SesionContexto';

export interface SolicitudPendiente {
  solicitudId: string;
  notaId: string;
  numeroVisible: string;
  version: number;
  solicitadaEn: string;
  solicitadaPor: string | null;
  solicitadaPorNombre: string | null;
  cliente: string;
  clienteRut: string | null;
  condicionPago: string;
  plazoDias: number;
  total: string;
  ambitos: string[];
  credito: {
    motivos: string[];
    monto: string;
    linea: string | null;
    deuda: string | null;
    vencido: string | null;
  } | null;
  preciosManuales: {
    numero: number;
    descripcion: string;
    precio: string | null;
    referencia: string | null;
    motivo: string | null;
  }[];
  descuentos: { numero: number; descripcion: string; tipo: string | null; valor: string | null }[];
  listaPrecio?: {
    deLaNota: { codigo: string; nombre: string } | null;
    delCliente: { codigo: string; nombre: string } | null;
  } | null;
  puedeFirmar: boolean;
  impedimento: string | null;
}

/** Por qué una fila no se puede autorizar, en palabras del operador. */
const IMPEDIMENTOS: Record<string, string> = {
  FIRMA_REQUIERE_PERSONA: 'La firma la hace una persona con sesión',
  APROBADOR_NO_ACTIVO: 'Tu usuario no está activo',
  SIN_PERMISO_APROBAR: 'No tienes permiso para aprobar notas de venta',
  FIRMANTE_SIN_PERMISO_DEL_AMBITO: 'No tienes el permiso para autorizar este tipo de bloqueo',
  AUTOAPROBACION_PROHIBIDA: 'La regla exige que la autorice otra persona',
};

type Resultado = { ok: true } | { ok: false; mensaje: string };

export function useBandejaAprobaciones(empresaId: string, refrescarCada: number | false = 10_000) {
  return useQuery({
    queryKey: ['emp', empresaId, 'ventas', 'aprobaciones'] as const,
    queryFn: () =>
      api.get<{ items: SolicitudPendiente[] }>('/ventas/aprobaciones', { query: { empresaId } }),
    refetchInterval: refrescarCada,
    staleTime: 5_000,
  });
}

/** El porqué del bloqueo: cada ámbito con sus cifras (crédito) o sus líneas (precio, descuento). */
export function PorQue({ s }: { s: SolicitudPendiente }) {
  return (
    <ul className="motivos" data-por-que>
      {s.ambitos.map((a) => (
        <li key={a} data-ambito={a}>
          <strong>{NOMBRE_AMBITO[a] ?? a}</strong>
          {a === 'CREDITO' && s.credito && (
            <>
              {s.credito.motivos.length > 0 && (
                <>: {s.credito.motivos.map((m) => MOTIVOS_CREDITO[m] ?? m).join(' · ')}</>
              )}
              <div className="apagado">
                {s.credito.linea !== null && <>Línea {pesos(s.credito.linea)} · </>}
                {s.credito.deuda !== null ? (
                  <>
                    Deuda {pesos(s.credito.deuda)}
                    {s.credito.vencido !== null && <> (vencido {pesos(s.credito.vencido)})</>}
                    {' · '}
                  </>
                ) : (
                  <>Sin deuda conocida · </>
                )}
                Esta venta {pesos(s.credito.monto)}
              </div>
            </>
          )}
          {a === 'PRECIO_MANUAL' &&
            s.preciosManuales.map((l) => (
              <div key={l.numero} className="apagado">
                L{l.numero} {l.descripcion}: {pesos(l.precio)}
                {l.referencia && <> (lista {pesos(l.referencia)})</>}
                {l.motivo && <> · «{l.motivo}»</>}
              </div>
            ))}
          {a === 'LISTA_PRECIO' && s.listaPrecio && (
            <div className="apagado">
              Del cliente:{' '}
              {s.listaPrecio.delCliente
                ? `${s.listaPrecio.delCliente.codigo} ${s.listaPrecio.delCliente.nombre}`
                : '—'}{' '}
              → en la nota:{' '}
              {s.listaPrecio.deLaNota
                ? `${s.listaPrecio.deLaNota.codigo} ${s.listaPrecio.deLaNota.nombre}`
                : '—'}
            </div>
          )}
          {a === 'DESCUENTO' &&
            s.descuentos.map((l) => (
              <div key={l.numero} className="apagado">
                L{l.numero} {l.descripcion}:{' '}
                {l.tipo === 'PORCENTAJE' ? `${l.valor}%` : pesos(l.valor)}
              </div>
            ))}
        </li>
      ))}
    </ul>
  );
}

export function AprobacionesVentas({
  alAbrirNota,
}: {
  alAbrirNota: (notaId: string, numero: string) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const q = useBandejaAprobaciones(empresaId);
  const filas = q.data?.items ?? [];
  const firmables = filas.filter((f) => f.puedeFirmar);
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [confirmar, setConfirmar] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [resultados, setResultados] = useState<Record<string, Resultado>>({});
  const [numeros, setNumeros] = useState<Record<string, string>>({});

  // Lo marcado que ya no está pendiente (lo firmó otro, se retiró) deja de contar.
  const elegidas = filas.filter((f) => f.puedeFirmar && marcadas.has(f.solicitudId));
  const totalElegido = elegidas.reduce((t, f) => t + Number(f.total), 0);
  const alternar = (id: string) =>
    setMarcadas((m) => {
      const n = new Set(m);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const todas = elegidas.length === firmables.length && firmables.length > 0;

  const autorizar = async () => {
    setProcesando(true);
    const r: Record<string, Resultado> = {};
    // Una a una: cada firma es un comando auditado con su versión; un error no frena a las demás.
    for (const f of elegidas) {
      try {
        await api.post(
          `/ventas/notas/${f.notaId}/aprobar`,
          { version: f.version },
          { idempotencia: crypto.randomUUID() },
        );
        r[f.solicitudId] = { ok: true };
      } catch (e) {
        const p = presentarError(e);
        r[f.solicitudId] = {
          ok: false,
          mensaje: p.mensaje ? `${p.titulo}: ${p.mensaje}` : p.titulo,
        };
      }
    }
    setNumeros(Object.fromEntries(elegidas.map((f) => [f.solicitudId, f.numeroVisible])));
    setResultados(r);
    setMarcadas(new Set());
    setConfirmar(false);
    setProcesando(false);
    await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
  };

  const hechas = Object.values(resultados).filter((x) => x.ok).length;
  const fallidas = Object.entries(resultados).flatMap(([id, x]) =>
    x.ok ? [] : [{ id, mensaje: x.mensaje }],
  );

  const columnas: Columna<SolicitudPendiente>[] = [
    {
      id: 'sel',
      titulo: '',
      ancho: '2.5rem',
      alinear: 'centro',
      celda: (f) => (
        <input
          type="checkbox"
          aria-label={`Marcar ${f.numeroVisible}`}
          checked={f.puedeFirmar && marcadas.has(f.solicitudId)}
          disabled={!f.puedeFirmar || procesando}
          title={f.impedimento ? (IMPEDIMENTOS[f.impedimento] ?? f.impedimento) : undefined}
          onChange={() => alternar(f.solicitudId)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      id: 'nv',
      titulo: 'Nota',
      ancho: '9rem',
      celda: (f) => (
        <button
          type="button"
          className="enlace mono"
          onClick={() => alAbrirNota(f.notaId, f.numeroVisible)}
        >
          {f.numeroVisible}
        </button>
      ),
    },
    {
      id: 'cliente',
      titulo: 'Cliente',
      celda: (f) => (
        <>
          {f.cliente}
          <div className="apagado mono">{rut(f.clienteRut)}</div>
        </>
      ),
    },
    { id: 'total', titulo: 'Total', alinear: 'der', ancho: '8rem', celda: (f) => pesos(f.total) },
    {
      id: 'cond',
      titulo: 'Condición',
      ancho: '8rem',
      celda: (f) => (
        <>
          {NOMBRE_CONDICION[f.condicionPago] ?? f.condicionPago}
          {f.plazoDias > 0 && <div className="apagado">{f.plazoDias} días</div>}
        </>
      ),
    },
    { id: 'porque', titulo: 'Por qué se bloqueó', celda: (f) => <PorQue s={f} /> },
    {
      id: 'pidio',
      titulo: 'Pidió',
      ancho: '11rem',
      celda: (f) => (
        <>
          {f.solicitadaPorNombre ?? '—'}
          <div className="apagado">{fechaHora(f.solicitadaEn)}</div>
        </>
      ),
    },
    {
      id: 'puede',
      titulo: '',
      ancho: '12rem',
      celda: (f) =>
        f.puedeFirmar ? (
          <span className="apagado">Puedes autorizar</span>
        ) : (
          <span className="apagado" data-impedimento={f.impedimento}>
            {IMPEDIMENTOS[f.impedimento ?? ''] ?? f.impedimento}
          </span>
        ),
    },
  ];

  return (
    <div className="pantalla consulta" data-pantalla="aprobaciones-ventas">
      <p className="apagado">
        Lo que espera autorización. Marca las que quieras y autorízalas: no hace falta escribir un
        motivo, queda registrado que tú autorizaste, con fecha y hora. Se actualiza sola.
      </p>
      <div className="acciones-fila">
        <button
          type="button"
          disabled={!firmables.length || procesando}
          onClick={() =>
            setMarcadas(todas ? new Set() : new Set(firmables.map((f) => f.solicitudId)))
          }
        >
          {todas ? 'Desmarcar todas' : `Marcar todas las que puedo autorizar (${firmables.length})`}
        </button>
        <button
          type="button"
          className="primario"
          disabled={!elegidas.length || procesando}
          onClick={() => setConfirmar(true)}
        >
          Autorizar marcadas ({elegidas.length})
        </button>
      </div>
      {(hechas > 0 || fallidas.length > 0) && (
        <div role="status" data-resultado-autorizacion>
          {hechas > 0 && (
            <p>{hechas === 1 ? '1 nota autorizada.' : `${hechas} notas autorizadas.`}</p>
          )}
          {fallidas.map((x) => (
            <p key={x.id} className="alerta">
              {numeros[x.id] ?? 'Una nota'}: {x.mensaje}
            </p>
          ))}
        </div>
      )}
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Pendientes de autorización"
        columnas={columnas}
        filas={filas}
        clave={(f) => f.solicitudId}
        cargando={q.isLoading}
        vacio="No hay nada esperando autorización"
        alAbrir={(f) => alAbrirNota(f.notaId, f.numeroVisible)}
      />
      {confirmar && (
        <Modal
          titulo="Autorizar los bloqueos"
          ancho="angosto"
          alCerrar={() => !procesando && setConfirmar(false)}
          pie={
            <>
              <button type="button" onClick={() => setConfirmar(false)} disabled={procesando}>
                Cancelar
              </button>
              <button
                type="button"
                className="primario"
                data-autofoco
                disabled={procesando}
                onClick={() => void autorizar()}
              >
                {procesando ? 'Autorizando…' : 'Autorizar'}
              </button>
            </>
          }
        >
          <p>
            {elegidas.length === 1 ? '1 nota' : `${elegidas.length} notas`} por{' '}
            {pesos(String(totalElegido))}: {elegidas.map((f) => f.numeroVisible).join(', ')}.
          </p>
          <p className="ayuda">Queda registrado que tú las autorizaste, con fecha y hora.</p>
        </Modal>
      )}
    </div>
  );
}
